// Considered: StatCardAtom alone — rejected because an indicator needs a target line, a conditional band, a reason and a disclosure, each with its own state logic; this molecule composes StatCardAtom through its children slot instead. See spec Reuse Audit.
import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { SphereIndicator } from 'types/metric';
import StatCardAtom from '../../atoms/StatCard/StatCardAtom';
import StatusBadgeAtom from '../../atoms/status-badge/StatusBadgeAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { formatMetricValue } from '../../../utils/formatMetricValue';
import { formatRelativeTime } from '../../../utils/formatRelativeTime';
import { resolveLocalizedString } from '../../../utils/resolveLocalizedString';
import './IndicatorCardMolecule.scss';

export interface IndicatorCardMoleculeProps {
  indicator: SphereIndicator;
  /** Start with the Details disclosure open (stories and tests). */
  defaultDetailsOpen?: boolean;
  /** Re-reads the indicators; the card offers it as "Try again" when one of its reads failed. */
  onRetry?: () => void;
  /** A re-read is in flight, so the "Try again" button shows it is working. */
  retryPending?: boolean;
  className?: string;
}

const KEY = 'sphere.indicators';

const DIRECTION_ICON: Record<SphereIndicator['metric']['direction'], string> = {
  higher_is_better: 'pi pi-arrow-up',
  lower_is_better: 'pi pi-arrow-down',
  band: 'pi pi-arrows-h',
};

/** A read behind this card failed: its access check, the latest value, the period aggregate, the target or the stored evaluation. */
const hasFailedRead = (indicator: SphereIndicator): boolean => {
  if (indicator.access === 'unavailable') return true;
  if (indicator.access !== 'readable') return false;
  return (
    indicator.valueState === 'unavailable' ||
    indicator.periodValueState === 'unavailable' ||
    indicator.targetState === 'unavailable' ||
    (indicator.evaluation?.state === 'not_available' && indicator.evaluation.reason === 'evaluation_unavailable')
  );
};

const IndicatorCardMolecule: React.FC<IndicatorCardMoleculeProps> = ({
  indicator,
  defaultDetailsOpen = false,
  onRetry,
  retryPending = false,
  className = '',
}) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(defaultDetailsOpen);
  const instanceId = useId();
  const detailsId = `${instanceId}-details`;
  const articleRef = useRef<HTMLElement>(null);
  const retrying = useRef(false);

  // A successful re-read unmounts "Try again"; when that drops focus to the page, it lands on the card's first control.
  useEffect(() => {
    if (retryPending || !retrying.current) return;
    retrying.current = false;
    const active = document.activeElement;
    if (!active || active === document.body) articleRef.current?.querySelector<HTMLElement>('button')?.focus();
  }, [retryPending]);

  // A restricted entry carries no numbers; the section filters them out, so the card never speaks for them.
  if (indicator.access === 'restricted') return null;

  const locale = i18n?.language ?? 'en-US';
  const name = resolveLocalizedString(indicator.name, indicator.aspectSystemName, locale);
  const readable = indicator.access === 'readable';
  const periodFailed = readable && indicator.periodValueState === 'unavailable';
  const periodValue = readable && !periodFailed ? indicator.periodValue ?? null : null;
  const isAggregate = periodValue != null && periodValue.method !== 'last';
  const valueFailed = !readable || periodFailed || (!isAggregate && indicator.valueState === 'unavailable');
  const latest = !readable || indicator.valueState === 'unavailable' ? null : indicator.value ?? null;
  const value = valueFailed || isAggregate ? null : latest;
  const period = readable ? indicator.period ?? null : null;
  const evaluation = readable ? indicator.evaluation : undefined;
  const reason = evaluation?.state === 'not_available' ? evaluation.reason : null;
  const isPerson = reason === 'person_sphere';

  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' });
  const dateTimeFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  // The whole "this quarter" phrase per level, so a language can inflect the demonstrative (fr "ce trimestre", "cette année").
  const thisPeriod = t(`${KEY}.thisPeriod.${period ? period.level : 'period'}`, { defaultValue: t(`${KEY}.thisPeriod.period`) });
  const periodLabel = (p: NonNullable<typeof period>) => {
    const start = new Date(p.start);
    if (p.level === 'quarter') {
      return t(`${KEY}.period.quarter`, { n: Math.floor(start.getUTCMonth() / 3) + 1 });
    }
    if (p.level === 'year') return String(start.getUTCFullYear());
    return dateFormat.formatRange(start, new Date(p.end));
  };

  const aggregateEmpty = isAggregate && periodValue.observationCount === 0;
  const noEntriesLabel = t(`${KEY}.noEntriesInPeriod`, { thisPeriod });
  let headline: number | null = null;
  if (isAggregate) headline = periodValue.value;
  else if (value) headline = value.value;
  const formatted = headline !== null ? formatMetricValue({ value: headline, unit: indicator.metric.unit, locale, t }) : null;
  let headlineLabel: string;
  if (formatted) headlineLabel = formatted.full;
  else if (aggregateEmpty) headlineLabel = noEntriesLabel;
  else if (valueFailed) headlineLabel = t(`${KEY}.valueFailed`);
  else headlineLabel = t(`${KEY}.unknownValue`);
  const valueNode = (
    <>
      <span aria-hidden="true">{formatted ? formatted.display : '—'}</span>
      <span className="indicator-card-molecule__sr">{headlineLabel}</span>
    </>
  );

  let caption: React.ReactNode = null;
  if (valueFailed) {
    caption = t(`${KEY}.valueFailed`);
  } else if (indicator.source === 'rollup') {
    const rollup = indicator.rollup;
    if (rollup?.contributing != null) {
      caption = rollup.excluded
        ? t(`${KEY}.rolledUpFromExcluded`, {
            contributing: rollup.contributing,
            excluded: rollup.excluded,
          })
        : t(`${KEY}.rolledUpFrom`, { contributing: rollup.contributing });
    } else {
      caption = t(`${KEY}.rolledUp`);
    }
  } else if (isAggregate) {
    const count = periodValue.observationCount;
    if (aggregateEmpty) caption = noEntriesLabel;
    // Entries exist but the method cannot combine them for this metric (weighted; sum on a level).
    else if (periodValue.value === null) caption = t(`${KEY}.periodValue.uncombined`, { count, thisPeriod });
    else caption = t(`${KEY}.periodValue.${periodValue.method}`, { count, thisPeriod });
  } else if (value) {
    const ago = formatRelativeTime(value.observedAt, t);
    const beforePeriod = period != null && new Date(value.observedAt) < new Date(period.start);
    caption = t(`${KEY}.${beforePeriod ? 'recordedBeforePeriod' : 'recorded'}`, { ago });
  }

  let targetLine: string | null = null;
  if (readable) {
    switch (indicator.targetState) {
      case 'set':
        if (indicator.target && period) {
          const targetValue = formatMetricValue({
            value: indicator.target.value,
            unit: indicator.metric.unit,
            locale,
            t,
          });
          targetLine = t(`${KEY}.target.set`, {
            period: periodLabel(period),
            value: targetValue.display,
          });
        }
        break;
      case 'none':
        targetLine = t(`${KEY}.target.none`);
        break;
      case 'none_for_period':
        targetLine = t(`${KEY}.target.noneForPeriod`, { thisPeriod });
        break;
      case 'ambiguous':
        targetLine = t(`${KEY}.target.ambiguous`);
        break;
      case 'unavailable':
        targetLine = t(`${KEY}.target.unavailable`);
        break;
      default:
        break;
    }
  }

  const targetIsSet = readable && indicator.targetState === 'set';
  const showNotCompared = targetIsSet && reason != null && !isPerson;

  // A roll-up whose reducer entry could not be read carries no `rollup`, so no method to name.
  let sourceLabel: string;
  if (indicator.source !== 'rollup') sourceLabel = t(`${KEY}.source.recorded`);
  else if (indicator.rollup) sourceLabel = t(`${KEY}.source.rollup`, { method: indicator.rollup.method });
  else sourceLabel = t(`${KEY}.rolledUp`);

  const detailRows: Array<{ label: string; value: React.ReactNode }> = [];
  if (open && readable) {
    detailRows.push(
      {
        label: t(`${KEY}.details.direction`),
        value: (
          <>
            <i className={DIRECTION_ICON[indicator.metric.direction]} aria-hidden="true" /> {t(`${KEY}.direction.${indicator.metric.direction}`)}
          </>
        ),
      },
      {
        label: t(`${KEY}.details.kind`),
        value: t(`${KEY}.kind.${indicator.metric.kind}`),
      }
    );
    if (period) {
      detailRows.push({
        label: t(`${KEY}.details.period`),
        value: dateFormat.formatRange(new Date(period.start), new Date(period.end)),
      });
    }
    if (value) {
      detailRows.push({
        label: t(`${KEY}.details.recorded`),
        value: dateTimeFormat.format(new Date(value.observedAt)),
      });
    } else if (isAggregate && latest) {
      const latestValue = formatMetricValue({
        value: latest.value,
        unit: indicator.metric.unit,
        locale,
        t,
      });
      detailRows.push({
        label: t(`${KEY}.details.latestEntry`),
        value: t(`${KEY}.details.latestEntryValue`, {
          value: latestValue.display,
          date: dateTimeFormat.format(new Date(latest.observedAt)),
        }),
      });
    }
    detailRows.push({
      label: t(`${KEY}.details.source`),
      value: sourceLabel,
    });
    if (evaluation?.state === 'valid') {
      detailRows.push({
        label: t(`${KEY}.details.thresholds`),
        value: t(`${KEY}.thresholds`, {
          onTrack: evaluation.thresholds.onTrackAtOrAbove,
          atRisk: evaluation.thresholds.atRiskAtOrAbove,
        }),
      });
    }
    if (reason && !isPerson) {
      detailRows.push({
        label: t(`${KEY}.details.whyNotCompared`),
        value: t(`${KEY}.gap.${reason}`),
      });
    }
  }

  return (
    <article ref={articleRef} className={`indicator-card-molecule ${className}`.trim()} aria-label={name} data-access={indicator.access}>
      <StatCardAtom label={name} value={valueNode} caption={caption} tone="default">
        <div className="indicator-card-molecule__rows">
          {targetLine ? <div className="indicator-card-molecule__target">{targetLine}</div> : null}
          {evaluation?.state === 'valid' ? (
            <div className="indicator-card-molecule__comparison">
              <StatusBadgeAtom status={evaluation.status} variant="chip" />
              <span>{t(`${KEY}.attainment`, { pct: Math.round(evaluation.attainmentPct) })}</span>
            </div>
          ) : null}
          {showNotCompared ? <div className="indicator-card-molecule__not-compared">{t(`${KEY}.notComparedYet`)}</div> : null}
          {onRetry && hasFailedRead(indicator) ? (
            <ButtonAtom
              type="button"
              variant="text"
              size="small"
              label={t(`${KEY}.tryAgain`)}
              aria-label={t(`${KEY}.tryAgainFor`, { name })}
              loading={retryPending}
              onClick={() => {
                retrying.current = true;
                onRetry();
              }}
            />
          ) : null}
          {readable ? (
            <ButtonAtom
              variant="text"
              label={t(`${KEY}.${open ? 'hideDetails' : 'detailsToggle'}`)}
              aria-label={t(`${KEY}.${open ? 'hideDetailsFor' : 'detailsToggleFor'}`, { name })}
              aria-expanded={open}
              aria-controls={detailsId}
              onClick={() => setOpen((prev) => !prev)}
            />
          ) : null}
          <div id={detailsId}>
            {open ? (
              <dl className="indicator-card-molecule__details">
                {detailRows.map((row) => (
                  <React.Fragment key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            ) : null}
          </div>
        </div>
      </StatCardAtom>
    </article>
  );
};

IndicatorCardMolecule.displayName = 'IndicatorCardMolecule';

export default IndicatorCardMolecule;
